import React, { useState } from "react";
import { MdClose } from "react-icons/md";

const INPUT = {
  width: "100%", padding: "9px 12px",
  border: "1px solid #e0e0ee", borderRadius: "8px",
  fontSize: "13px", outline: "none", background: "#fafafa",
  fontFamily: "inherit", boxSizing: "border-box",
};

/**
 * CreateProjectModal — works in two modes:
 *  - mode="create"  (default): calls onCreate(data)
 *  - mode="edit":             calls onSave(data), pre-fills from initialData
 */
export default function CreateProjectModal({
  onClose,
  onCreate,        // create mode
  onSave,          // edit mode
  teamMembers = [],
  mode = "create",
  initialData = {},
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(() => ({
    title:              initialData.title              || "",
    clientName:         initialData.clientName         || "",
    totalContractValue: initialData.totalContractValue || "",
    status:             initialData.status             || "Ongoing",
    startDate:          initialData.startDate          || "",
    deliveryDate:       initialData.deliveryDate       || "",
    referrerName:       initialData.referrerName       || "",
    referralPhone:      initialData.referralPhone      || "",
    referralRate:       initialData.referralRate       || "",
    assignedManagers:   initialData.assignedManagers   || [],
  }));

  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleManager = (emp) => {
    setForm(f => {
      const exists = f.assignedManagers.find(m => m.id === emp.id);
      return {
        ...f,
        assignedManagers: exists
          ? f.assignedManagers.filter(m => m.id !== emp.id)
          : [...f.assignedManagers, { id: emp.id, name: emp.name || emp.full_name, avatar: emp.avatar || emp.avatar_url }],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await onSave({ ...initialData, ...form, totalContractValue: parseFloat(form.totalContractValue) || 0, referralRate: parseFloat(form.referralRate) || 0 });
      } else {
        await onCreate(form);
      }
      onClose();
    } catch (err) {
      alert(`Failed to ${isEdit ? "save" : "create"} project: ` + err.message);
    } finally {
      setSaving(false);
    }
  };

  const commission = form.referrerName && form.referralRate && form.totalContractValue
    ? ((parseFloat(form.totalContractValue) * parseFloat(form.referralRate)) / 100)
    : null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "580px", maxHeight: "92vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.22)" }}>

        {/* Header */}
        <div style={{ padding: "22px 28px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0f8", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <div>
            <h2 style={{ fontSize: "17px", fontWeight: 800, color: "#1a1a2e" }}>
              {isEdit ? "Edit Project" : "New Project"}
            </h2>
            <p style={{ fontSize: "12px", color: "#aaa", marginTop: "2px" }}>
              {isEdit ? "Update project details" : "Create a project manually"}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "#f5f5f5", border: "none", borderRadius: "50%", width: "32px", height: "32px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MdClose size={18} color="#666" />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "24px 28px" }}>
          <div style={{ display: "grid", gap: "14px" }}>

            {/* Title */}
            <div>
              <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Project Title *</label>
              <input style={{ ...INPUT, marginTop: "4px" }} value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Org Restructuring — Al-Noor Group" required />
            </div>

            {/* Client */}
            <div>
              <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Client Name</label>
              <input style={{ ...INPUT, marginTop: "4px" }} value={form.clientName} onChange={e => set("clientName", e.target.value)} placeholder="Client / Company name" />
            </div>

            {/* Contract Value + Status */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Contract Value (SAR)</label>
                <input style={{ ...INPUT, marginTop: "4px" }} type="number" value={form.totalContractValue} onChange={e => set("totalContractValue", e.target.value)} placeholder="0" />
              </div>
              <div>
                <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</label>
                <select style={{ ...INPUT, marginTop: "4px" }} value={form.status} onChange={e => set("status", e.target.value)}>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Delayed">Delayed</option>
                  <option value="Stopped">Stopped</option>
                </select>
              </div>
            </div>

            {/* Dates */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Start Date</label>
                <input style={{ ...INPUT, marginTop: "4px" }} type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Delivery Date</label>
                <input style={{ ...INPUT, marginTop: "4px" }} type="date" value={form.deliveryDate} onChange={e => set("deliveryDate", e.target.value)} />
              </div>
            </div>

            {/* Referral */}
            <div style={{ background: "#FFF8E1", borderRadius: "12px", padding: "16px" }}>
              <p style={{ fontSize: "11px", color: "#E65100", fontWeight: 700, marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>🤝 Referral Info (Optional)</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
                <div>
                  <label style={{ fontSize: "11px", color: "#666" }}>Referrer Name</label>
                  <input style={{ ...INPUT, marginTop: "4px" }} value={form.referrerName} onChange={e => set("referrerName", e.target.value)} placeholder="Who brought the lead?" />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#666" }}>Referrer Phone</label>
                  <input style={{ ...INPUT, marginTop: "4px" }} value={form.referralPhone} onChange={e => set("referralPhone", e.target.value)} placeholder="+966 5X XXX XXXX" type="tel" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: "11px", color: "#666" }}>Commission Rate (%)</label>
                <input style={{ ...INPUT, marginTop: "4px", maxWidth: "160px" }} type="number" min="0" max="100" value={form.referralRate} onChange={e => set("referralRate", e.target.value)} placeholder="0" />
              </div>
              {commission !== null && (
                <div style={{ marginTop: "10px", background: "#1a1a2e", borderRadius: "8px", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>Estimated Commission ({form.referralRate}%)</span>
                  <span style={{ fontSize: "16px", fontWeight: 800, color: "#60a5fa" }}>SAR {commission.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Managers */}
            {teamMembers.length > 0 && (
              <div>
                <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Assign Managers</label>
                <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {teamMembers.map(m => {
                    const empId = m.id || m.employee_id;
                    const isSelected = form.assignedManagers.some(x => x.id === empId);
                    return (
                      <button
                        key={empId}
                        type="button"
                        onClick={() => toggleManager({ id: empId, name: m.name || m.full_name, avatar: m.avatar || m.avatar_url })}
                        style={{
                          padding: "5px 12px", borderRadius: "999px",
                          border: isSelected ? "1.5px solid #3761E9" : "1.5px solid #e0e0ee",
                          background: isSelected ? "#EEF2FF" : "#fff",
                          color: isSelected ? "#3761E9" : "#666",
                          fontSize: "12px", fontWeight: isSelected ? 700 : 400,
                          cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
                        }}
                      >
                        {m.name || m.full_name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ marginTop: "24px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ padding: "10px 20px", borderRadius: "10px", border: "1px solid #e0e0ee", background: "#fff", color: "#666", fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ padding: "10px 28px", borderRadius: "10px", border: "none", background: "#3761E9", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
              {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Project")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
